import React from 'react';
import {
    Box,
    Chip,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material';
import {
    CTableWrapper,
    DeleteIcon,
    EditIcon,
} from '../../../../../dum';
import {
    formatDate,
    formatFloat,
    formatRelativeDate,
    round,
} from '../../../../../../helpers';
import { OrderAdjustmentsPageQuery } from '../../../../../../services';
import { ORDER_ADJUSTMENT_TYPE_DEVOLUCION_ID } from '../../../../../smart/forms/upsert/production/order-adjustment/order-adjustment-form';

type AdjustmentDoc =
    OrderAdjustmentsPageQuery['paginatedOrderAdjustments']['docs'][number];
type AdjustmentProduct = AdjustmentDoc['order_adjustment_products'][number];

// Value of a returned line, IVA included, priced at the sale line's own prices
// and taxed at the sale's effective rate (tax / subtotal). Same formula as the
// backend's net totals (computeOrderSaleNetTotals), so a sale's returns add up
// to `total_with_tax − net_total_with_tax`. Null when it is not a Devolución.
function makeReturnLineValue(orderAdjustment: AdjustmentDoc) {
    const orderSale = orderAdjustment.order_sale;
    if (
        orderAdjustment.order_adjustment_type?.id !==
            ORDER_ADJUSTMENT_TYPE_DEVOLUCION_ID ||
        !orderSale
    ) {
        return null;
    }
    const taxMultiplier =
        orderSale.subtotal !== 0 ? 1 + orderSale.tax / orderSale.subtotal : 1;
    return (oap: AdjustmentProduct) => {
        const line = orderSale.order_sale_products.find(
            (osp) => osp.product_id === oap.product_id,
        );
        if (!line) return 0;
        return (
            (oap.kilos * line.kilo_price + oap.groups * line.group_price) *
            taxMultiplier
        );
    };
}

interface IOrderAdjustmentCard {
    orderAdjustment: AdjustmentDoc;
    onEdit: (orderAdjustment: AdjustmentDoc) => void;
    onDelete: (orderAdjustment: AdjustmentDoc) => void;
}

/**
 * Card body for one adjustment inside the Ajustes CCardList. Header
 * (fecha/tipo/venta), and the products sub-table with actions.
 */
export function OrderAdjustmentCard({
    orderAdjustment,
    onEdit,
    onDelete,
}: IOrderAdjustmentCard) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const subBaseColor = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
    const subStripeColor = isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe';

    const returnLineValue = makeReturnLineValue(orderAdjustment);
    const returnTotal = returnLineValue
        ? round(
              orderAdjustment.order_adjustment_products.reduce(
                  (acc, oap) => acc + returnLineValue(oap),
                  0,
              ),
          )
        : null;

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1,
                }}
            >
                <Typography variant={'subtitle2'}>
                    {formatDate(orderAdjustment.date)}
                </Typography>
                {orderAdjustment.updated_at ? (
                    <Typography
                        variant={'caption'}
                        color={'text.secondary'}
                    >
                        (act. {formatRelativeDate(orderAdjustment.updated_at)}
                        {orderAdjustment.updated_by
                            ? ` por ${orderAdjustment.updated_by.fullname}`
                            : ''}
                        )
                    </Typography>
                ) : null}
                {orderAdjustment.order_adjustment_type ? (
                    <Chip
                        size={'small'}
                        label={orderAdjustment.order_adjustment_type.name}
                    />
                ) : null}
                {orderAdjustment.order_sale?.account?.name ? (
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {orderAdjustment.order_sale.account.name}
                    </Typography>
                ) : null}
                <Box sx={{ flex: 1 }} />
                {orderAdjustment.order_sale ? (
                    <Typography variant={'body2'}>
                        Venta #{orderAdjustment.order_sale.order_code}
                        {orderAdjustment.order_sale.invoice_code
                            ? ` · F-${orderAdjustment.order_sale.invoice_code}`
                            : ''}
                    </Typography>
                ) : null}
            </Box>
            <CTableWrapper
                padding={false}
                baseColor={subBaseColor}
                stripeColor={subStripeColor}
                items={[
                    {
                        title: 'Producto',
                        cellGrow: 60,
                        content: (oap) => oap.product?.compound_description,
                    },
                    {
                        title: 'Grupos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (oap) => formatFloat(oap.groups),
                    },
                    {
                        title: 'Kilos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (oap) => formatFloat(oap.kilos),
                    },
                    ...(returnLineValue
                        ? [
                              {
                                  title: 'Total (IVA incl.)',
                                  cellGrow: 15,
                                  textAlign: 'right' as const,
                                  content: (oap: AdjustmentProduct) =>
                                      formatFloat(round(returnLineValue(oap))),
                              },
                          ]
                        : []),
                ]}
                data={orderAdjustment.order_adjustment_products}
            />
            {returnTotal !== null ? (
                <Typography
                    variant={'body2'}
                    sx={{ textAlign: 'right', fontWeight: 600, mt: 1 }}
                >
                    Total devolución (IVA incl.): {formatFloat(returnTotal)}
                </Typography>
            ) : null}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    mt: 1,
                }}
            >
                <IconButton
                    size={'small'}
                    onClick={(e) => {
                        // The whole card is clickable (opens edit) — keep the
                        // buttons from double-firing through it.
                        e.stopPropagation();
                        onEdit(orderAdjustment);
                    }}
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    size={'small'}
                    disabled={!orderAdjustment.is_deletable}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(orderAdjustment);
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </Box>
        </Box>
    );
}
